<template>
  <div class="actualidad-container">
    <!-- Skeleton Loader -->
    <div v-if="loading">
      <div class="skeleton-first-post">
        <div class="skeleton-image"></div>
        <div class="skeleton-content">
          <div class="skeleton-category"></div>
          <div class="skeleton-title"></div>
          <div class="skeleton-title short"></div>
          <div class="skeleton-date"></div>
        </div>
      </div>
      <div class="posts-grid">
        <div v-for="i in 4" :key="i" class="skeleton-post">
          <div class="skeleton-image"></div>
          <div class="skeleton-content">
            <div class="skeleton-category"></div>
            <div class="skeleton-title"></div>
            <div class="skeleton-title short"></div>
            <div class="skeleton-date"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista de posts -->
    <div v-else-if="posts.length > 0">
      <!-- Primer post: ancho completo -->
      <div 
        v-if="posts[0]"
        class="first-post"
        @click="goToPost(posts[0])"
      >
        <img :src="getImageUrl(posts[0].image)" :alt="posts[0].title" class="first-post-image">
        <div class="first-post-content">
          <span class="post-category">{{ posts[0].category?.name || 'Deportes' }}</span>
          <h2 class="first-post-title">{{ posts[0].title }}</h2>
          <span class="post-date">{{ formatDate(posts[0].created_at) }}</span>
        </div>
      </div>

      <!-- Resto de posts en 2 columnas -->
      <div class="posts-grid">
        <div 
          v-for="post in posts.slice(1)" 
          :key="post.id"
          class="post-item"
          @click="goToPost(post)"
        >
          <img :src="getImageUrl(post.image)" :alt="post.title" class="post-image">
          <div class="post-content">
            <span class="post-category">{{ post.category?.name || 'Deportes' }}</span>
            <h3 class="post-title">{{ post.title }}</h3>
            <span class="post-date">{{ formatDate(post.created_at) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="empty-posts">
      No hay publicaciones deportivas disponibles
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../services/api'

const router = useRouter()
const posts = ref([])
const loading = ref(true)

const getImageUrl = (image) => {
  if (!image) return '/noimagen.png'
  if (image.startsWith('http')) return image
  return `https://portal.jlhvision.com/posts/${image}`
}

const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000 / 60 / 60)
  
  if (diff < 1) return 'Hace unos minutos'
  if (diff < 24) return `Hace ${diff} horas`
  if (diff < 48) return 'Ayer'
  return d.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const loadPosts = async () => {
  loading.value = true
  try {
    const response = await api.get('/sports')
    posts.value = response.data || []
  } catch (error) {
    console.error('Error cargando posts deportivos:', error)
    posts.value = []
  } finally {
    loading.value = false
  }
}

const goToPost = (post) => {
  router.push(`/noticia/${post.slug}`)
}

onMounted(() => {
  loadPosts()
})
</script>

<style scoped>
.actualidad-container {
  width: 100%;
  margin: 15px 0;
}

/* ========== PRIMER POST (ANCHO COMPLETO) ========== */
.first-post {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: transform 0.2s;
}

.first-post:active {
  transform: scale(0.99);
}

.first-post-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  object-position: center;
}

.first-post-content {
  padding: 15px;
}

.first-post-title {
  font-size: 18px;
  font-weight: bold;
  color: #1a1a1a;
  margin: 8px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ========== GRID 2 COLUMNAS ========== */
.posts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.post-item {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}

.post-item:active {
  transform: scale(0.98);
}

.post-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  object-position: center;
}

.post-content {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* ========== ESTILOS COMUNES ========== */
.post-category {
  display: inline-block;
  background: transparent;
  color: #a50000;
  font-size: 10px;
  padding: 0;
  margin: 0;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.5px;
}

.post-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-date {
  font-size: 10px;
  color: #999;
  margin: 0;
}

/* ========== SKELETON ========== */
.skeleton-first-post {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.skeleton-first-post .skeleton-image {
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-first-post .skeleton-content {
  padding: 15px;
}

.skeleton-post {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.skeleton-post .skeleton-image {
  width: 100%;
  height: 120px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-content {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skeleton-category {
  width: 70px;
  height: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title {
  width: 90%;
  height: 14px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title.short {
  width: 60%;
}

.skeleton-date {
  width: 70px;
  height: 10px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.empty-posts {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 14px;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 480px) {
  .first-post-image {
    height: 180px;
  }
  
  .first-post-title {
    font-size: 16px;
  }
  
  .first-post-content {
    padding: 12px;
  }
  
  .posts-grid {
    gap: 10px;
  }
  
  .post-image {
    height: 100px;
  }
  
  .post-content {
    padding: 8px;
  }
  
  .post-title {
    font-size: 12px;
  }
  
  .post-category {
    font-size: 9px;
  }
  
  .post-date {
    font-size: 9px;
  }
}
</style>